import type { GexStrikeData } from '../types/gex';

/**
 * The price range a gamma profile should draw.
 *
 * Lives outside GexProfileWidget so it can be tested directly, and so that
 * widget keeps Fast Refresh — a non-component export from a component file
 * silently disables it.
 */

/** Half-width of the ceiling band, as a fraction of spot. */
export const PROFILE_RANGE_PCT = 0.15;

/**
 * Share of total gamma the price axis must contain.
 *
 * Measured, not picked. On SPY's 0DTE chain the fit barely moves between 80%
 * and 99% (755..773 vs 744..773) because the gamma is genuinely concentrated —
 * so the threshold only has to be tight enough to ignore the listed tail.
 * Ninety is comfortably inside that plateau.
 *
 * ⚠️ Raising it toward 100% does NOT converge on "show everything sensible" —
 * it converges on the full listed chain, which for SPY runs 50 to 1480.
 */
const PROFILE_MASS = 0.9;

/** Floor on the half-width, so a degenerate chain cannot produce a sliver. */
const PROFILE_MIN_PCT = 0.015;

/**
 * The price range worth showing: where the gamma is, not every listed strike.
 *
 * ⚠️ **A fixed band around spot is wrong for short-dated chains.** SPY's 0DTE
 * chain lists 173 strikes from 500 to 950, but **99% of its gamma sits in 28
 * strikes between 745 and 776** — under ±2% of spot. Drawn on a ±15% band, the
 * entire structure is squeezed into a fifth of the panel and the walls sit on
 * top of each other. The all-expiries profile looks fine on the same band only
 * because its gamma genuinely is spread that wide, which is what makes the
 * fault look like a rendering quirk rather than a scaling one.
 *
 * Ranked by magnitude rather than trimmed by percentile: gamma is not
 * symmetric around spot, and a fixed percentile cuts whichever side happens to
 * be heavier.
 *
 * ⚠️ **The ±`PROFILE_RANGE_PCT` band is a ceiling, and it is load-bearing —
 * do not remove it in favour of the mass fit alone.** The instinct is that
 * capturing the gamma should replace the band entirely. It does on short-dated
 * chains, where the band never binds. It fails badly on all-expiries, where
 * the gamma really is spread that far: at 90% mass, MSFT's full chain fits
 * 350..675 against a spot of 496, and SPY's 600..850. Both are far worse than
 * the band they would replace. Measured 2026-08-04:
 *
 *   SPY 0DTE      90% mass ->  750 ..  773    band never binds
 *   SPY all-exp   90% mass ->  600 ..  850    band clamps to 657 ..  889
 *   MSFT all-exp  90% mass ->  350 ..  675    band clamps to 422 ..  571
 *
 * So this can only ever TIGHTEN the axis against the old fixed band — it
 * cannot hide structure that used to be on screen. Spot is always inside it.
 */
export function profileRange(
  gex: GexStrikeData[] | undefined,
  spot: number
): [number, number] {
  const lo = spot * (1 - PROFILE_RANGE_PCT);
  const hi = spot * (1 + PROFILE_RANGE_PCT);
  if (!gex?.length) return [lo, hi];

  // |call| + |put|, not the net: the net cancels at strikes carrying heavy
  // gamma on both sides, which are exactly the ones that must stay on screen.
  const rows = gex
    .map((d) => ({
      strike: Number(d.strike),
      weight: Math.abs(Number(d.call_gex) || 0) + Math.abs(Number(d.put_gex) || 0),
    }))
    .filter((r) => Number.isFinite(r.strike) && r.weight > 0);

  const total = rows.reduce((sum, r) => sum + r.weight, 0);
  if (!(total > 0)) return [lo, hi];

  let held = 0;
  let low = spot;
  let high = spot;

  for (const r of [...rows].sort((a, b) => b.weight - a.weight)) {
    held += r.weight;
    low = Math.min(low, r.strike);
    high = Math.max(high, r.strike);
    if (held / total >= PROFILE_MASS) break;
  }

  // Breathing room so the outermost bar is not flush against the frame.
  const pad = (high - low) * 0.08;
  low -= pad;
  high += pad;

  // ⚠️ TRYING IT WITHOUT THE BAND CEILING — the axis is now purely where the
  // gamma is. `PROFILE_RANGE_PCT` survives only as the no-data fallback above.
  //
  // Measured before removing it, so the comparison is on record (2026-08-04):
  //
  //   SPY 0DTE      mass  750 ..  773   band  657 ..  889   band never bound
  //   SPY all-exp   mass  600 ..  850   band  657 ..  889   band was clamping
  //   MSFT all-exp  mass  350 ..  675   band  422 ..  571   band was clamping
  //
  // So short-dated profiles are unchanged by this; the all-expiries panels get
  // wider. To restore the ceiling, wrap the two values below in
  // `Math.max(..., lo)` and `Math.min(..., hi)`.
  return [
    Math.min(low, spot * (1 - PROFILE_MIN_PCT)),
    Math.max(high, spot * (1 + PROFILE_MIN_PCT)),
  ];
}
